import { Editor, Element, Path, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { TableCursor, TableEditor } from "slate-table";
import {
  TableCellElement,
  TableElement,
  TableRowElement,
} from "components/ManualBuilder/types";

const selectedTable = (editor: Editor) => {
  if (!editor.selection) return;

  const match = (node: unknown): node is TableElement =>
    Element.isElement(node) && node.type === "table";
  const anchor = Editor.above<TableElement>(editor, {
    at: editor.selection.anchor,
    match,
  });
  const focus = Editor.above<TableElement>(editor, {
    at: editor.selection.focus,
    match,
  });

  if (anchor && focus && Path.equals(anchor[1], focus[1])) return anchor;
};

const tableCells = (editor: Editor, tablePath: Path) =>
  Array.from(
    Editor.nodes<TableCellElement>(editor, {
      at: tablePath,
      match: (node) => Element.isElement(node) && node.type === "table-cell",
    })
  );

const hasHeader = (editor: Editor, tablePath: Path) =>
  tableCells(editor, tablePath).some(([cell]) => cell.isHeader === true);

export const isTableHeaderEnabled = (editor: Editor) => {
  const table = selectedTable(editor);
  return !!table && hasHeader(editor, table[1]);
};

const setHeader = (editor: Editor, tablePath: Path, enabled: boolean) => {
  const [row] = Editor.nodes<TableRowElement>(editor, {
    at: tablePath,
    match: (node) => Element.isElement(node) && node.type === "table-row",
  });

  for (const [cell, path] of tableCells(editor, tablePath)) {
    const isHeader = enabled && !!row && Path.isDescendant(path, row[1]);
    if ((cell.isHeader === true) !== isHeader) {
      Transforms.setNodes<TableCellElement>(editor, { isHeader }, { at: path });
    }
  }
};

export const insertTableWithHeader = (editor: Editor) => {
  if (TableCursor.isInTable(editor)) return;

  HistoryEditor.withNewBatch(editor, () => {
    Editor.withoutNormalizing(editor, () => {
      TableEditor.insertTable(editor, { rows: 3, cols: 3 });
      const table = selectedTable(editor);
      if (table) setHeader(editor, table[1], true);
    });
  });
};

export const toggleTableHeaderRow = (editor: Editor) => {
  const table = selectedTable(editor);
  if (!table) return;

  const enabled = hasHeader(editor, table[1]);
  HistoryEditor.withNewBatch(editor, () => {
    Editor.withoutNormalizing(editor, () => {
      setHeader(editor, table[1], !enabled);
    });
  });
};

const updateTable = (editor: Editor, action: (at: Path) => void) => {
  const table = selectedTable(editor);
  if (!table || !editor.selection) return;

  const [cell] = Editor.nodes<TableCellElement>(editor, {
    at: editor.selection,
    match: (node) => Element.isElement(node) && node.type === "table-cell",
  });
  if (!cell) return;

  const enabled = hasHeader(editor, table[1]);
  const tableRef = Editor.pathRef(editor, table[1]);
  try {
    HistoryEditor.withNewBatch(editor, () => {
      Editor.withoutNormalizing(editor, () => {
        action(cell[1]);
        if (tableRef.current) {
          setHeader(editor, tableRef.current, enabled);
        }
      });
    });
  } finally {
    tableRef.unref();
  }
};

export const insertTableColumn = (editor: Editor, before = false) =>
  updateTable(editor, (at) => TableEditor.insertColumn(editor, { at, before }));

export const removeTableColumn = (editor: Editor) =>
  updateTable(editor, (at) => TableEditor.removeColumn(editor, { at }));

export const insertTableRow = (editor: Editor, before = false) =>
  updateTable(editor, (at) => TableEditor.insertRow(editor, { at, before }));

export const removeTableRow = (editor: Editor) =>
  updateTable(editor, (at) => TableEditor.removeRow(editor, { at }));
